import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Container, Divider, Typography } from '@mui/material'
import { SettingsOutlined } from '@mui/icons-material'
import { OnboardingBanner, useOnboardingState } from '@/features/onboarding'
import { groupContainerSx, groupDividerSx, groupLabelSx } from '../components/settingsSectionStyles'
import {
  ApiKeysSection,
  AppearanceSection,
  EncryptionSection,
  HostIdentitiesSection,
  SystemSection,
} from '../components'

export const SettingsPage: FC = () => {
  const { isOnboardingComplete, wizardSteps, isReturningUser, onboardingFlags } =
    useOnboardingState()

  const renderSecurityGroupContent = () => {
    if (isOnboardingComplete) {
      return (
        <>
          <EncryptionSection />
          <Divider sx={groupDividerSx} />
          <ApiKeysSection />
          <Divider sx={groupDividerSx} />
          <HostIdentitiesSection />
        </>
      )
    }

    if (onboardingFlags) {
      return (
        <>
          <OnboardingBanner
            wizardSteps={wizardSteps}
            isReturningUser={isReturningUser}
            onboardingFlags={onboardingFlags}
          />
          <EncryptionSection />
        </>
      )
    }

    return <EncryptionSection />
  }

  return (
    <Container component="main" maxWidth="md" sx={containerSx}>
      <Box sx={headerSx}>
        <SettingsOutlined sx={headerIconSx} />
        <Typography variant="h6" sx={titleSx}>
          Settings
        </Typography>
      </Box>

      <Box sx={groupContainerSx}>
        <Typography variant="overline" sx={groupLabelSx}>
          General
        </Typography>
        <AppearanceSection />
      </Box>

      <Box sx={groupContainerSx}>
        <Typography variant="overline" sx={groupLabelSx}>
          Security
        </Typography>
        {renderSecurityGroupContent()}
      </Box>

      <Box sx={groupContainerSx}>
        <Typography variant="overline" sx={groupLabelSx}>
          System
        </Typography>
        <SystemSection />
      </Box>
    </Container>
  )
}

const containerSx: SxProps<Theme> = {
  py: 4,
}

const headerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mb: 3,
  pb: 2,
  borderBottom: 1,
  borderColor: 'divider',
}

const headerIconSx: SxProps<Theme> = {
  fontSize: 22,
  color: 'text.disabled',
}

const titleSx: SxProps<Theme> = {
  fontWeight: 500,
  color: 'text.secondary',
}
